import { Badge, Banner, Button, Checkbox, Dialog, Field, Input, InputArea, Select } from '@cloudflare/kumo';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import * as XLSX from 'xlsx';
import { DataTable as Table } from '../../components/ui/DataTable';
import { DangerButton } from '../../components/ui/DangerButton';
import { ICONS } from '../../components/ui/icons';
import { PasswordField } from '../../components/ui/PasswordField';
import { DangerConfirmModal } from '../../components/DangerConfirmModal';
import { useTenant } from '../../contexts/TenantContext';
import { useAuthenticatedFetch } from '../../hooks/useAuthenticatedFetch';
import { useAppToast } from '../../hooks/useAppToast';
import { brlFromCents } from '../../lib/tools';
import { SkeletonText } from '../../components/ui/Skeleton';

type ChargeStatus = 'CREATING' | 'PROCESSING' | 'PENDING' | 'PAID' | 'OVERDUE' | 'CANCELLED' | 'REFUNDED' | 'ERROR';
interface StatBucket { count: number; amountCents: number }
interface Batch {
  id: string; name: string; sessionName: string; status: 'CREATING' | 'SENDING' | 'DONE' | 'FAILED';
  total: number; created: number; failed: number; error: string | null; createdAt: string;
  stats: Record<'PENDING' | 'PAID' | 'OVERDUE' | 'CANCELLED' | 'REFUNDED' | 'ERROR', StatBucket>;
}
interface Overview {
  gateway: null | {
    sandbox: boolean; accountName: string | null; webhookOk: boolean; lastError: string | null;
    sendConfirmation: boolean; confirmMessage: string; webhookUrl: string;
  };
  sessions: Array<{ name: string; displayName: string | null; provider: string; status: string }>;
  defaultMessage: string;
  maxRows: number;
  batches: Batch[];
}
interface Preview { valid: number; totalCents: number; errors: Array<{ line: number; error: string }> }
interface ChargeRow { id: string; name: string; phone: string; amountCents: number; dueDate: string; status: ChargeStatus; error: string | null; paidAt: string | null; invoiceUrl: string | null }

const STATUS: Record<ChargeStatus, { label: string; color: 'green' | 'blue' | 'orange' | 'red' | 'neutral' }> = {
  CREATING: { label: 'Criando', color: 'neutral' },
  PROCESSING: { label: 'Criando', color: 'neutral' },
  PENDING: { label: 'Aguardando', color: 'blue' },
  PAID: { label: 'Pago', color: 'green' },
  OVERDUE: { label: 'Vencida', color: 'orange' },
  CANCELLED: { label: 'Cancelada', color: 'neutral' },
  REFUNDED: { label: 'Estornada', color: 'neutral' },
  ERROR: { label: 'Erro', color: 'red' },
};
const BATCH_STATUS: Record<Batch['status'], string> = { CREATING: 'Criando cobranças', SENDING: 'Enviando', DONE: 'Concluído', FAILED: 'Falhou' };

// Cabeçalhos aceitos na planilha (sem acento, minúsculos)
const COLUMN_ALIASES: Record<string, string> = {
  nome: 'nome', cliente: 'nome', name: 'nome',
  telefone: 'telefone', celular: 'telefone', whatsapp: 'telefone', fone: 'telefone', phone: 'telefone',
  cpf: 'cpfCnpj', cnpj: 'cpfCnpj', cpfcnpj: 'cpfCnpj', documento: 'cpfCnpj', doc: 'cpfCnpj',
  valor: 'valor', value: 'valor', preco: 'valor',
  vencimento: 'vencimento', data: 'vencimento', datavencimento: 'vencimento', due: 'vencimento',
  descricao: 'descricao', referencia: 'descricao', produto: 'descricao',
};
const normKey = (k: string) => k.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/[^a-z]/g, '');

function readSheet(file: File): Promise<Record<string, unknown>[]> {
  return file.arrayBuffer().then((buf) => {
    const wb = XLSX.read(buf, { type: 'array' });
    const ws = wb.Sheets[wb.SheetNames[0]];
    const raw = XLSX.utils.sheet_to_json<Record<string, unknown>>(ws, { defval: '', raw: true });
    return raw.map((r) => Object.fromEntries(
      Object.entries(r).map(([k, v]) => [COLUMN_ALIASES[normKey(k)] ?? normKey(k), typeof v === 'string' ? v.trim() : v]),
    ));
  });
}

function downloadTemplate() {
  const due = new Date(Date.now() + 7 * 86_400_000).toLocaleDateString('pt-BR');
  const ws = XLSX.utils.aoa_to_sheet([
    ['nome', 'telefone', 'cpf_cnpj', 'valor', 'vencimento', 'descricao'],
    ['Maria Souza', '11999998888', '529.982.247-25', '149,90', due, 'Mensalidade de outubro'],
  ]);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Cobranças');
  XLSX.writeFile(wb, 'modelo-cobrancas-pix.xlsx');
}

/** Ferramentas › Cobranças Pix (Asaas da organização) */
export function CobrancasPage() {
  const { can } = useTenant();
  const fetchApi = useAuthenticatedFetch();
  const toast = useAppToast();
  const [data, setData] = useState<Overview | null>(null);
  const [connectOpen, setConnectOpen] = useState(false);
  const [apiKey, setApiKey] = useState('');
  const [sandbox, setSandbox] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [rows, setRows] = useState<Record<string, unknown>[] | null>(null);
  const [fileName, setFileName] = useState('');
  const [preview, setPreview] = useState<Preview | null>(null);
  const [form, setForm] = useState({ name: '', sessionName: '', message: '' });
  const [confirm, setConfirm] = useState<{ send: boolean; message: string } | null>(null);
  const [disconnecting, setDisconnecting] = useState(false);
  const [detail, setDetail] = useState<{ batch: Batch; charges: ChargeRow[] } | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const canConnect = can('integrations:manage');
  const canCharge = can('campaigns:write');

  const load = useCallback(async () => {
    const res = await fetchApi('/api/tools/cobrancas');
    if (!res.ok) return;
    const d: Overview = await res.json();
    setData(d);
    setForm((f) => ({ ...f, message: f.message || d.defaultMessage, sessionName: f.sessionName || d.sessions.find((s) => s.status === 'WORKING')?.name || '' }));
    if (d.gateway) setConfirm((c) => c ?? { send: d.gateway!.sendConfirmation, message: d.gateway!.confirmMessage });
  }, [fetchApi]);
  useEffect(() => { load(); }, [load]);

  // Lotes criando ou enviando: atualiza os números sozinho
  const active = (data?.batches ?? []).some((b) => b.status === 'CREATING' || b.status === 'SENDING');
  useEffect(() => {
    if (!active) return;
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [active, load]);

  const call = async (key: string, url: string, init: RequestInit, ok?: string) => {
    setBusy(key);
    try {
      const res = await fetchApi(url, init);
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.message || 'Não foi possível concluir');
      if (ok) toast.success(ok);
      return body;
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Não foi possível concluir');
      return null;
    } finally {
      setBusy(null);
    }
  };

  const connect = async (e: React.FormEvent) => {
    e.preventDefault();
    const body = await call('connect', '/api/tools/cobrancas/gateway', { method: 'PUT', body: JSON.stringify({ apiKey, sandbox }) });
    if (!body) return;
    toast.success(`Asaas conectado${body.accountName ? `: ${body.accountName}` : ''}`);
    if (body.lastError) toast.error(body.lastError);
    setConnectOpen(false); setApiKey('');
    load();
  };

  const onFile = async (file: File | undefined) => {
    if (!file) return;
    try {
      const parsed = await readSheet(file);
      setRows(parsed); setFileName(file.name);
      setForm((f) => ({ ...f, name: f.name || file.name.replace(/\.[^.]+$/, '') }));
      const body = await call('preview', '/api/tools/cobrancas/preview', { method: 'POST', body: JSON.stringify({ rows: parsed }) });
      setPreview(body);
    } catch {
      toast.error('Não foi possível ler a planilha. Use .xlsx ou .csv com cabeçalho na primeira linha.');
    }
  };

  const create = async (ignoreErrors: boolean) => {
    if (!rows) return;
    const body = await call('create', '/api/tools/cobrancas', {
      method: 'POST', body: JSON.stringify({ ...form, rows, ignoreErrors }),
    }, 'Lote criado: as cobranças estão sendo geradas no Asaas e o envio começa em seguida');
    if (!body) return;
    setRows(null); setPreview(null); setFileName(''); setForm((f) => ({ ...f, name: '' }));
    if (fileRef.current) fileRef.current.value = '';
    load();
  };

  const openDetail = async (b: Batch) => {
    const res = await fetchApi(`/api/tools/cobrancas/${b.id}`);
    if (res.ok) setDetail({ batch: b, charges: (await res.json()).charges });
  };

  const exportCsv = async (b: Batch) => {
    const res = await fetchApi(`/api/tools/cobrancas/${b.id}/export`);
    if (!res.ok) return toast.error('Não foi possível exportar');
    const url = URL.createObjectURL(await res.blob());
    const a = document.createElement('a');
    a.href = url; a.download = `cobrancas-${b.name}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  if (!data) return <div className="py-8"><SkeletonText lines={4} /></div>;
  const gw = data.gateway;

  const sessionOptions = data.sessions.map((s) => ({
    value: s.name,
    label: `${s.displayName || s.name} (${s.provider}${s.status !== 'WORKING' ? ', desconectado' : ''})`,
  }));

  return (
    <>
      <div style={{ padding: '28px 40px 0', marginBottom: 24 }}>
        <Link to="/ferramentas" className="tools-back"><ICONS.ArrowLeft size={16} /> Ferramentas</Link>
        <h2 className="text-xl font-semibold" style={{ marginBottom: 4, fontSize: 24, fontWeight: 600 }}>Cobranças Pix</h2>
        <div style={{ color: 'var(--text-muted)' }}>
          Suba uma planilha e cada cliente recebe no WhatsApp o Pix copia e cola da cobrança, gerado no seu Asaas. O dinheiro cai direto na sua conta, e a baixa é automática.
        </div>
      </div>

      <div className="page-content space-y-6">
        {/* Conta Asaas */}
        <section className="brand-card">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <div className="flex items-center gap-2">
                <h4 className="text-base font-semibold">Conta do Asaas</h4>
                {gw ? <Badge variant="green">Conectada</Badge> : <Badge variant="neutral">Não conectada</Badge>}
                {gw?.sandbox && <Badge variant="orange">Sandbox</Badge>}
              </div>
              <div style={{ color: 'var(--text-muted)', marginTop: 4 }}>
                {gw ? (gw.accountName || 'Conta conectada') : 'Conecte a conta do Asaas da sua empresa para gerar os Pix.'}
              </div>
            </div>
            {canConnect && (
              <div className="flex gap-2">
                <Button variant="secondary" icon={gw ? ICONS.Edit : ICONS.Plus} onClick={() => setConnectOpen(true)}>
                  {gw ? 'Trocar chave' : 'Conectar Asaas'}
                </Button>
                {gw && (
                  <DangerButton variant="dangerOutline" loading={busy === 'disconnect'}
                    onClick={() => setDisconnecting(true)}>
                    Desconectar
                  </DangerButton>
                )}
              </div>
            )}
          </div>
          {gw && !gw.webhookOk && (
            <div style={{ marginTop: 12 }}>
              <Banner variant="alert" description={<>
                {gw.lastError || 'O aviso de pagamento não foi cadastrado no Asaas.'} Endereço: <code>{gw.webhookUrl}</code>
              </>} />
            </div>
          )}
          {gw && confirm && canConnect && (
            <div className="charges-confirm">
              <Checkbox onCheckedChange={(checked: boolean) => setConfirm({ ...confirm, send: checked })} checked={confirm.send} label={<>
                Enviar confirmação no WhatsApp quando o cliente pagar
              </>} />
              {confirm.send && (
                <InputArea label="Mensagem de confirmação" description="Variáveis: {{nome}} e {{valor}}" onChange={(e) => setConfirm({ ...confirm, message: e.target.value })} rows={2} value={confirm.message} />
              )}
              <div>
                <Button variant="secondary" loading={busy === 'confirm'} onClick={() => call('confirm', '/api/tools/cobrancas/gateway', { method: 'PATCH', body: JSON.stringify({ sendConfirmation: confirm.send, confirmMessage: confirm.message }) }, 'Confirmação salva')}>
                  Salvar confirmação
                </Button>
              </div>
            </div>
          )}
        </section>

        {/* Novo lote */}
        {gw && canCharge && (
          <section className="brand-card">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div>
                <h4 className="text-base font-semibold">Nova cobrança em lote</h4>
                <div style={{ color: 'var(--text-muted)', marginTop: 2 }}>
                  Planilha com nome, telefone, CPF/CNPJ, valor e vencimento (descrição é opcional). Até {data.maxRows.toLocaleString('pt-BR')} linhas.
                </div>
              </div>
              <Button variant="secondary" icon={ICONS.Download} onClick={downloadTemplate}>Baixar modelo</Button>
            </div>

            {data.sessions.length === 0 ? (
              <div style={{ marginTop: 16 }}>
                <Banner variant="alert" description={<>Nenhum número disponível para enviar as cobranças. Conecte um número oficial em WhatsApp › Conexões.</>} />
              </div>
            ) : (
              <div className="space-y-4" style={{ marginTop: 16 }}>
                <label className="charges-drop">
                  <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" onChange={(e) => onFile(e.target.files?.[0])} />
                  <ICONS.Upload size={16} />
                  <span>{fileName || 'Escolher planilha (.xlsx ou .csv)'}</span>
                </label>

                {preview && (
                  <div className={`charges-preview ${preview.errors.length ? 'charges-preview--warn' : ''}`}>
                    <strong>{preview.valid.toLocaleString('pt-BR')} cobrança{preview.valid !== 1 ? 's' : ''} válida{preview.valid !== 1 ? 's' : ''} · {brlFromCents(preview.totalCents)}</strong>
                    {preview.errors.length > 0 && (
                      <>
                        <span>{preview.errors.length} linha{preview.errors.length !== 1 ? 's' : ''} com erro (ficam de fora se você continuar):</span>
                        <ul>{preview.errors.slice(0, 8).map((e) => <li key={e.line}>Linha {e.line}: {e.error}</li>)}</ul>
                      </>
                    )}
                  </div>
                )}

                {preview && preview.valid > 0 && (
                  <>
                    <div className="grid gap-4 md:grid-cols-2">
                      <Field label="Nome do lote">
                        <Input onChange={(e) => setForm({ ...form, name: e.target.value })} value={form.name} />
                      </Field>
                      <Field label="Enviar pelo número">
                        <Select value={(sessionOptions.find((o) => o.value === form.sessionName) ?? null)?.value ?? null} items={sessionOptions} onValueChange={(value: string | null) => setForm({ ...form, sessionName: value ?? '' })} />
                      </Field>
                    </div>
                    <InputArea label="Mensagem" description="Variáveis: {{nome}}, {{valor}}, {{vencimento}}, {{descricao}}, {{pix}} (copia e cola) e {{link}} (fatura do Asaas)" onChange={(e) => setForm({ ...form, message: e.target.value })} rows={7} value={form.message} />
                    <div className="flex justify-end gap-2">
                      <Button variant="secondary" onClick={() => { setRows(null); setPreview(null); setFileName(''); if (fileRef.current) fileRef.current.value = ''; }}>Cancelar</Button>
                      <Button variant="primary" loading={busy === 'create'} disabled={!form.sessionName} onClick={() => create(preview.errors.length > 0)}>
                        Criar {preview.valid.toLocaleString('pt-BR')} cobrança{preview.valid !== 1 ? 's' : ''} e enviar
                      </Button>
                    </div>
                  </>
                )}
              </div>
            )}
          </section>
        )}

        {/* Lotes */}
        {data.batches.length > 0 && (
          <section>
            <h4 className="text-base font-semibold" style={{ marginBottom: 12 }}>Lotes</h4>
            <div className="bg-white rounded-lg border border-[var(--border)] overflow-hidden">
              <Table
                variant="borderless"
                items={data.batches}
                columnDefinitions={[
                  {
                    id: 'lote',
                    header: 'Lote',
                    cell: (b: Batch) => (
                      <>
                        <div className="font-medium">{b.name}</div>
                        <div className="text-xs !text-[var(--text-muted)]">{new Date(b.createdAt).toLocaleString('pt-BR')} · {b.sessionName}</div>
                      </>
                    ),
                  },
                  {
                    id: 'situacao',
                    header: 'Situação',
                    cell: (b: Batch) => (
                      <>
                        <Badge variant={b.status === 'FAILED' ? 'red' : b.status === 'DONE' ? 'neutral' : 'blue'}>{BATCH_STATUS[b.status]}</Badge>
                        {b.error && <div className="text-xs !text-[var(--crm-text-error)]">{b.error}</div>}
                      </>
                    ),
                  },
                  {
                    id: 'cobrancas',
                    header: 'Cobranças',
                    cell: (b: Batch) => (
                      <div>{b.created.toLocaleString('pt-BR')} de {b.total.toLocaleString('pt-BR')}{b.failed ? ` · ${b.failed} com erro` : ''}</div>
                    ),
                  },
                  {
                    id: 'pagas',
                    header: 'Pagas',
                    cell: (b: Batch) => (
                      <div style={{ color: 'var(--brand-text)', fontWeight: 600 }}>
                        {b.stats.PAID.count} · {brlFromCents(b.stats.PAID.amountCents)}
                      </div>
                    ),
                  },
                  {
                    id: 'em-aberto',
                    header: 'Em aberto',
                    cell: (b: Batch) => {
                      const open = { count: b.stats.PENDING.count + b.stats.OVERDUE.count, amountCents: b.stats.PENDING.amountCents + b.stats.OVERDUE.amountCents };
                      return <div>{open.count} · {brlFromCents(open.amountCents)}</div>;
                    },
                  },
                  {
                    id: 'acoes',
                    header: ' ',
                    cell: (b: Batch) => (
                      <div className="flex gap-2 justify-end">
                        <Button variant="secondary" onClick={() => openDetail(b)}>Ver</Button>
                        <Button variant="secondary" icon={ICONS.Download} onClick={() => exportCsv(b)}>CSV</Button>
                      </div>
                    ),
                  },
                ]}
                ariaLabels={{ tableLabel: 'Lotes de cobrança', selectionGroupLabel: 'Lotes de cobrança' }}
              />
            </div>
          </section>
        )}
      </div>

      <Dialog.Root open={connectOpen} onOpenChange={(o: boolean) => { if (!o) (() => { if (connectOpen) setConnectOpen(false); })(); }}>
<Dialog size="base" className="p-6">
<Dialog.Title className="mb-4 text-lg font-semibold">{<h3 className="text-lg font-semibold">Conectar o Asaas</h3>}</Dialog.Title>

        <form noValidate onSubmit={connect} className="flex flex-col gap-4">
          <div style={{ color: 'var(--text-muted)' }}>
            No Asaas, abra <strong>Integrações › Chaves de API</strong>, gere uma chave e cole abaixo. A plataforma cadastra sozinha o aviso de pagamento (webhook) na sua conta.
          </div>
          <PasswordField
            label="Chave de API do Asaas"
            placeholder="$aact_..."
            value={apiKey}
            onChange={setApiKey}
            autoComplete="off"
          />
          <Checkbox onCheckedChange={(checked: boolean) => setSandbox(checked)} checked={sandbox} label={<>
            Conta de testes (sandbox do Asaas)
          </>} />
          <div className="flex justify-end gap-2">
            <Button variant="secondary" type="button" onClick={() => setConnectOpen(false)}>Cancelar</Button>
            <Button type="submit" variant="primary" loading={busy === 'connect'} disabled={apiKey.trim().length < 20}>Conectar</Button>
          </div>
        </form>
      
</Dialog>
</Dialog.Root>

      <Dialog.Root open={!!detail} onOpenChange={(o: boolean) => { if (!o) (() => { if (detail) setDetail(null); })(); }}>
<Dialog size="lg" className="p-6">
<Dialog.Title className="mb-4 text-lg font-semibold">{<h3 className="text-lg font-semibold" style={{ marginBottom: 12 }}>{detail?.batch.name}</h3>}</Dialog.Title>

        {detail && (
          <>
            <div className="bg-white rounded-lg border border-[var(--border)] overflow-hidden" style={{ maxHeight: '60vh', overflowY: 'auto' }}>
              <Table
                variant="borderless"
                items={detail.charges}
                columnDefinitions={[
                  {
                    id: 'cliente',
                    header: 'Cliente',
                    cell: (c: ChargeRow) => (
                      <>
                        <div className="font-medium">{c.name}</div>
                        <div className="text-xs !text-[var(--text-muted)]">+{c.phone}</div>
                      </>
                    ),
                  },
                  {
                    id: 'valor',
                    header: 'Valor',
                    cell: (c: ChargeRow) => <div>{brlFromCents(c.amountCents)}</div>,
                  },
                  {
                    id: 'vencimento',
                    header: 'Vencimento',
                    cell: (c: ChargeRow) => <div>{new Date(c.dueDate).toLocaleDateString('pt-BR', { timeZone: 'UTC' })}</div>,
                  },
                  {
                    id: 'situacao',
                    header: 'Situação',
                    cell: (c: ChargeRow) => (
                      <>
                        <Badge variant={STATUS[c.status].color}>{STATUS[c.status].label}</Badge>
                        {c.paidAt && <div className="text-xs !text-[var(--text-muted)]">em {new Date(c.paidAt).toLocaleDateString('pt-BR')}</div>}
                        {c.error && <div className="text-xs !text-[var(--crm-text-error)]">{c.error}</div>}
                        {c.invoiceUrl && c.status !== 'PAID' && <a className="project-link text-xs" href={c.invoiceUrl} target="_blank" rel="noreferrer">fatura</a>}
                      </>
                    ),
                  },
                ]}
                ariaLabels={{ tableLabel: 'Cobranças do lote', selectionGroupLabel: 'Cobranças do lote' }}
              />
            </div>
          </>
        )}
      
</Dialog>
</Dialog.Root>
      <DangerConfirmModal open={disconnecting} title="Desconectar o Asaas" confirmText="Desconectar" objectName="Asaas"
        onCancel={() => setDisconnecting(false)}
        onConfirm={async () => { setDisconnecting(false); if (await call('disconnect', '/api/tools/cobrancas/gateway', { method: 'DELETE' }, 'Asaas desconectado')) { setConfirm(null); load(); } }}>
        A plataforma deixa de gerar Pix e de receber a baixa automática dos pagamentos. As cobranças já criadas continuam no Asaas.
      </DangerConfirmModal>
    </>
  );
}
